import { usePathname, useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { useCallback } from 'react'

import { RequestTypeAlias } from '@/api/models/requests/lib/requests.types'
import {
  AVAILABLE_PASS_TYPES,
  PERMANENT_PASS_TYPES
} from '@/components/entities/passes/model/constants'
import { PageType } from '@/components/entities/requests/model/types'

/**
 * Хук для парсинга параметров URL и определения состояния отображения в RequestDrawer
 *
 * @param pageType - тип страницы (Requests или Passes)
 * @returns объект с параметрами:
 * - type: тип обращения/пропуска из URL параметра type
 * - isValidType: флаг валидности типа обращения/пропуска
 * - isValidPasses: флаг нахождения на странице создания пропуска
 * - requestId: ID обращения
 * - hasRepeatParam: флаг наличия параметра repeat (режим повторения)
 *
 * Определяет что показывать в Drawer:
 * - Форму создания - если есть type и он валидный
 * - Детальную карточку - если есть requestId
 * - Список доступных пропусков - если isValidPasses === true
 * - Форму пропуска - если есть type и он валидный для пропусков
 * - Режим повторения - если есть hasRepeatParam
 */
const useRequestParams = (pageType: PageType) => {
  const pathname = usePathname()
  const router = useRouter()
  const searchParams = useSearchParams()
  const typeAliasFromUrl = searchParams?.get('type') as RequestTypeAlias
  const repeatId = searchParams?.get('repeat')
  const hasRepeatParam = searchParams?.has('repeat')
  const isRentSummaryStep = searchParams?.get('step') === 'summary'
  const isRequestEditing = searchParams?.get('editing') === 'true'
  const editingAutoId = searchParams?.get('autoId') || undefined
  const bookingId = searchParams?.get('bookingId') || undefined
  const paymentInvoiceId = searchParams?.get('invoiceId') || undefined
  const paymentPersonalAccountId =
    searchParams?.get('personalAccountId') || undefined

  const isValidTypeAlias = [
    RequestTypeAlias.CommonRequest,
    ...AVAILABLE_PASS_TYPES,
    ...PERMANENT_PASS_TYPES
  ].includes(typeAliasFromUrl)

  const isValidPasses =
    Boolean(searchParams?.has('new')) && pageType === PageType.Passes

  const getRequestId = (): string | undefined => {
    if (repeatId) {
      return repeatId
    }
    if (pageType === PageType.Passes) {
      return router.query?.passId as string
    }

    if (pageType === PageType.Requests) {
      return router.query?.requestId as string
    }
    return undefined
  }

  const navigateBackFromRentSummary = () => {
    const params = new URLSearchParams(searchParams?.toString())
    params.delete('step')
    router.push(`${pathname}?${params}`)
  }

  const navigateBackFromEditing = useCallback(() => {
    const params = new URLSearchParams(searchParams?.toString())
    params.delete('editing')
    params.delete('autoId')
    router.push(`${pathname}?${params}`)
  }, [pathname, router, searchParams])

  return {
    typeAliasFromUrl,
    isValidTypeAlias,
    isValidPasses,
    requestId: getRequestId(),
    bookingId,
    paymentInvoiceId,
    paymentPersonalAccountId,
    hasRepeatParam,
    isRentSummaryStep,
    isRequestEditing,
    editingAutoId,
    navigateBackFromRentSummary,
    navigateBackFromEditing
  }
}

export { useRequestParams }
